/**
 * AmbientParticles — lightweight floating-dust overlay.
 *
 * Reuses the "soft round particle" technique already used across the app
 * (canvas + radial-gradient dots, additive blending) — see the starfield in
 * book-canvas.tsx and the ink-smoke field in ink-generation.tsx — but scaled
 * down to a small, ambient, always-on loop meant to sit on top of a single
 * small image (e.g. a plan/add-on illustration card).
 *
 * - Very few particles (default 8) — cheap to render, sized to its parent box.
 * - Respects `prefers-reduced-motion`: renders nothing (no canvas work at all).
 * - Uses ResizeObserver to stay crisp if its container resizes.
 * - Cancels its rAF loop and disconnects the observer on unmount.
 */
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface Dot {
  x: number; // normalized [0, 1] horizontal position
  y: number; // normalized [0, 1] vertical position
  r: number; // base radius (css px, pre-dpr)
  vy: number; // upward drift speed
  vx: number; // sideways drift speed
  phase: number; // twinkle phase offset
  speed: number; // twinkle speed multiplier
}

interface AmbientParticlesProps {
  className?: string;
  /** Particle count — keep low, this overlays a small area. */
  count?: number;
  /** RGB triplet string (no alpha), e.g. "223,201,147". */
  color?: string;
}

export function AmbientParticles({ className, count = 8, color = '223,201,147' }: AmbientParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Bail out entirely for reduced-motion users — no canvas, no rAF loop.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    function resize() {
      if (!canvas) return;
      const { clientWidth, clientHeight } = canvas;
      canvas.width = Math.max(1, clientWidth * dpr);
      canvas.height = Math.max(1, clientHeight * dpr);
    }
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const dots: Dot[] = Array.from({ length: count }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: 1 + Math.random() * 1.6,
      vy: 0.05 + Math.random() * 0.07,
      vx: (Math.random() - 0.5) * 0.02,
      phase: Math.random() * Math.PI * 2,
      speed: 0.6 + Math.random() * 0.5,
    }));

    let raf = 0;
    let running = true;

    function frame(t: number) {
      if (!running || !canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'lighter';

      for (const d of dots) {
        d.y -= d.vy * 0.01;
        d.x += d.vx * 0.01;
        if (d.y < -0.05) {
          d.y = 1.05;
          d.x = Math.random();
        }
        if (d.x < -0.05) d.x = 1.05;
        if (d.x > 1.05) d.x = -0.05;

        const twinkle = 0.3 + 0.35 * Math.sin((t / 900) * d.speed + d.phase);
        const px = d.x * canvas.width;
        const py = d.y * canvas.height;
        const radius = d.r * dpr * 2.2;

        const g = ctx.createRadialGradient(px, py, 0, px, py, radius);
        g.addColorStop(0, `rgba(${color},${twinkle})`);
        g.addColorStop(1, `rgba(${color},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(px, py, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [count, color]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn('pointer-events-none absolute inset-0 h-full w-full', className)}
    />
  );
}
